import { API_URL } from '../config';

export const OFFLINE_DAY_OPTIONS = [1, 3, 7, 14];
export const DEFAULT_OFFLINE_PREFERENCES = { enabled: true, days: 7, scope: 'mine' };
export const offlinePreferencesKey = (userId) => `bw_offline_preferences:${userId}`;
export const isOfflineAdminPlusUser = (userType = null) => {
  const resolved = String(userType ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('user_type') : '') ?? '').trim().toLowerCase();
  return ['admin', 'developer'].includes(resolved);
};

export const normalizeOfflinePreferences = (value = {}, isAdmin = null) => {
  const allowedIsAdmin = isAdmin !== null ? Boolean(isAdmin) : isOfflineAdminPlusUser();
  const validScopes = allowedIsAdmin ? ['mine', 'available', 'all'] : ['mine', 'available'];
  const fallbackScope = allowedIsAdmin ? 'all' : 'mine';
  return {
    enabled: value?.enabled !== false,
    days: OFFLINE_DAY_OPTIONS.includes(Number(value?.days)) ? Number(value.days) : 7,
    scope: validScopes.includes(value?.scope) ? value.scope : fallbackScope,
  };
};

export const readOfflinePreferences = (userId, userType = null) => {
  const isAdmin = isOfflineAdminPlusUser(userType);
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(offlinePreferencesKey(userId)) : null;
    if (!raw) return { ...DEFAULT_OFFLINE_PREFERENCES, scope: isAdmin ? 'all' : 'mine' };
    return normalizeOfflinePreferences(JSON.parse(raw), isAdmin);
  }
  catch { return { ...DEFAULT_OFFLINE_PREFERENCES, scope: isAdmin ? 'all' : 'mine' }; }
};

export const offlineAbortError = () => new DOMException('Загрузка приостановлена', 'AbortError');

const abortable = (promise, signal, timeoutMs) => new Promise((resolve, reject) => {
  const abort = () => finish(reject, offlineAbortError());
  const timer = setTimeout(() => finish(reject, new Error('Подготовка приложения заняла слишком много времени. Повторите загрузку.')), timeoutMs);
  const finish = (callback, value) => {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
    callback(value);
  };
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  promise.then(value => finish(resolve, value), error => finish(reject, error));
});

export const prepareAppForOffline = async (signal, onProgress) => {
  if (!('serviceWorker' in navigator)) {
    return { ready: false, build: 'no-sw', completed: 0, total: 0 };
  }
  let registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) {
    try {
      registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    } catch (e) {
      console.warn('SW registration attempt failed:', e);
    }
  }
  if (!registration) {
    try {
      registration = await abortable(navigator.serviceWorker.ready, signal, 5000);
    } catch {
      return { ready: false, build: 'sw-not-ready', completed: 0, total: 0 };
    }
  }
  const worker = registration?.installing || registration?.waiting || registration?.active;
  if (!worker) {
    return { ready: false, build: 'no-worker', completed: 0, total: 0 };
  }
  const channel = new MessageChannel();
  try {
    return await abortable(new Promise((resolve, reject) => {
      channel.port1.onmessage = ({ data }) => {
        if (data.type === 'progress') onProgress?.(data);
        if (data.type === 'ready') resolve(data);
        if (data.type === 'error') reject(new Error(data.message));
      };
      worker.postMessage({ type: 'PREPARE_OFFLINE' }, [channel.port2]);
    }), signal, 25000);
  } catch (err) {
    console.warn('prepareAppForOffline failed or timed out:', err);
    return { ready: false, build: 'sw-timeout', completed: 0, total: 0 };
  } finally {
    channel.port1.close();
    channel.port2.close();
  }
};

export const downloadOfflinePack = async ({ token, userId, preferences, signal, onProgress }) => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timer = setTimeout(abort, 90000);
  try {
    const params = new URLSearchParams({ days: preferences.days, scope: preferences.scope });
    const response = await fetch(`${API_URL}/api/orders/offline-work-cache?${params}`, {
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      cache: 'no-store', signal: controller.signal,
    });
    if (!response.ok) {
      const error = new Error(response.status === 401 ? 'Сессия завершена. Войдите в приложение.' : 'Не удалось загрузить данные офлайн-режима. Попробуйте ещё раз.');
      error.status = response.status;
      throw error;
    }
    const total = response.headers.get('content-encoding') ? 0 : Number(response.headers.get('content-length')) || 0;
    let bytes = 0;
    let payload;
    if (response.body?.getReader) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parts = [];
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          bytes += value.byteLength;
          parts.push(decoder.decode(value, { stream: true }));
          onProgress({ bytes, total });
        }
        parts.push(decoder.decode());
        payload = JSON.parse(parts.join(''));
      } finally { reader.releaseLock(); }
    } else {
      const raw = await response.text();
      bytes = new Blob([raw]).size;
      payload = JSON.parse(raw);
      onProgress({ bytes, total: bytes });
    }
    if (payload.schema_version !== 1 || Number(payload.user_id) !== Number(userId)
      || !['items', 'order_lists', 'order_details', 'task_details', 'stage_roles'].every(key => Array.isArray(payload[key]))
      || payload.preferences?.days !== preferences.days || payload.preferences?.scope !== preferences.scope) {
      throw new Error('Получен неполный офлайн-пакет. Предыдущая копия сохранена.');
    }
    const ids = new Set(payload.order_details.map(order => order.id));
    if (payload.order_lists.some(list => !Array.isArray(list.orders) || list.orders.some(order => !ids.has(order.id)))) {
      throw new Error('Не все карточки заказов загружены. Повторите загрузку.');
    }
    return { ...payload, bytes };
  } catch (error) {
    if (signal.aborted) throw offlineAbortError();
    if (controller.signal.aborted) throw new Error('Сервер не отвечает. Предыдущая копия сохранена.');
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  }
};

export const downloadOfflineDelta = async ({ token, userId, preferences, savedSnapshot, signal, onProgress }) => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
  const timer = setTimeout(abort, 60000);
  try {
    const knownOrders = {};
    if (Array.isArray(savedSnapshot?.orderDetails)) {
      for (const order of savedSnapshot.orderDetails) {
        if (order?.id) {
          knownOrders[String(order.id)] = order.sync_hash || order.updated_date || '';
        }
      }
    }

    const body = {
      days: preferences.days,
      scope: preferences.scope,
      expenses_revision: savedSnapshot?.expensesRevision || '',
      tasks_revision: savedSnapshot?.tasksRevision || '',
      known_orders: knownOrders,
    };

    const response = await fetch(`${API_URL}/api/orders/offline-work-cache/delta`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
      cache: 'no-store',
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = new Error(`Дельта-синхронизация вернула статус ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const raw = await response.text();
    const bytes = new Blob([raw]).size;
    const delta = JSON.parse(raw);
    if (typeof onProgress === 'function') {
      onProgress({ bytes, total: bytes });
    }

    if (delta.schema_version !== 1 || Number(delta.user_id) !== Number(userId)) {
      throw new Error('Получен некорректный ответ дельта-синхронизации.');
    }

    return { ...delta, bytes };
  } catch (error) {
    if (signal.aborted) throw offlineAbortError();
    if (controller.signal.aborted) throw new Error('Сервер не отвечает при дельта-синхронизации.');
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', abort);
  }
};

export const checkOfflinePackVersion = async ({ token, preferences, signal }) => {
  const params = new URLSearchParams({ days: preferences.days, scope: preferences.scope });
  const response = await fetch(`${API_URL}/api/orders/offline-work-cache/version?${params}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    cache: 'no-store', signal,
  });
  if (!response.ok) {
    const error = new Error(response.status === 401 ? 'Сессия завершена. Войдите в приложение.' : 'Не удалось проверить обновления офлайн-данных.');
    error.status = response.status;
    throw error;
  }
  const version = await response.json();
  if (version?.schema_version !== 1 || !version.revision) throw new Error('Сервер вернул некорректную версию офлайн-пакета.');
  return version;
};
